import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Badge,
  Button,
  DatePicker,
  Drawer,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { IoFilter } from "react-icons/io5";

import { DownloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchManagers } from "../../store/Thunks/appThunk";

const ACTIVE_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const STATUS_TYPE_OPTIONS = [
  { label: "Freedom", value: "freedom" },
  { label: "Enterprise", value: "enterprise" },
];

const TIER_OPTIONS = [
  { label: "Basic", value: "tier1" },
  { label: "Pro", value: "tier2" },
  { label: "Advanced", value: "tier3" },
  { label: "Ultimate", value: "tier4" },
  // { lable: "Universe", value: "tier5" },
];

const SUBSCRIPTION_OPTIONS = [
  { label: "No Subscription", value: "no_subscription" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half Yearly", value: "halfyearly" },
  { label: "Yearly", value: "yearly" },
];

const RangePresets = {
  Today: () => [moment().startOf("day"), moment().endOf("day")],
  "Last 7 Days": () => [
    moment().subtract(7, "days").startOf("day"),
    moment().endOf("day"),
  ],
  "Last 30 Days": () => [
    moment().subtract(30, "days").startOf("day"),
    moment().endOf("day"),
  ],
  "Last 90 Days": () => [
    moment().subtract(90, "days").startOf("day"),
    moment().endOf("day"),
  ],
};

const generateQuery = (filters, search) => {
  const tempSearch = Object.entries(filters).reduce((acc, [key, value]) => {
    if (value && (moment.isMoment(value) || value instanceof Date)) {
      acc[key] = value.toISOString();
    } else if (value) {
      acc[key] = value;
    }

    return acc;
  }, {});
  const { accountManager, tiers, platformSetups, paymentPlans, ...rest } =
    tempSearch;
  const query = new URLSearchParams(rest);

  if (accountManager && accountManager.length > 0) {
    accountManager.forEach((manager) => {
      query.append("accountManager", manager);
    });
  }

  if (tiers && tiers.length > 0) {
    tiers.forEach((tier) => {
      query.append("tiers", tier);
    });
  }

  if (platformSetups && platformSetups.length > 0) {
    platformSetups.forEach((setup) => {
      query.append("platformSetups", setup);
    });
  }

  if (paymentPlans && paymentPlans.length > 0) {
    paymentPlans.forEach((plan) => {
      query.append("paymentPlans", plan);
    });
  }

  if (search) {
    query.append("search", search);
  }

  return query.toString();
};

const CSDashboardFilter = ({ setPaginationQuery, handleExportToCSV }) => {
  const dispatch = useAppDispatch();
  const { managerState } = useAppSelector((state) => state.app);
  const { managers } = managerState;
  const [search, setSearch] = useState("");
  const [csvConfirmModalVisible, setCsvConfirmModalVisible] = useState(false);
  const [csvProgressModalVisible, setCsvProgressModalVisible] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [finalFilterOptions, setFinalFilterOptions] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    startDate: RangePresets["Last 30 Days"]()[0],
    endDate: RangePresets["Last 30 Days"]()[1],
    activeStatus: null,
    paymentPlans: [],
    sortOn: "",
    sortOrder: "",
    platformSetups: [],
    tiers: [],
    accountManager: [],
    minEarnings: null,
    maxEarnings: null,
    tierExpiryStartDate: "",
    tierExpiryEndDate: "",
    tierLastRenewedAtStartDate: "",
    tierLastRenewedAtEndDate: "",
    lastActiveAtStartDate: "",
    lastActiveAtEndDate: "",
  });

  const isAnyFilterApplied = useMemo(() => {
    return Object.values(filterOptions).some(
      (value) => value && value !== "" && value.length > 0 && value !== null
    );
  }, [filterOptions]);

  const handleApplyFilters = useCallback(() => {
    setOpenFilterDrawer(false);
    const tempFinalFilterOptions = {
      ...finalFilterOptions,
      ...filterOptions,
    };
    setFinalFilterOptions({
      ...tempFinalFilterOptions,
    });
    const query = generateQuery(tempFinalFilterOptions, search);
    setPaginationQuery((prev) => ({
      ...prev,

      // if the filter query changes, then reset the page and limit to 1 and 10
      ...(prev.query !== query ? { page: 1, limit: 10 } : {}),

      query,
    }));
  }, [filterOptions, search]);

  const handleCancelFilters = useCallback(() => {
    setOpenFilterDrawer(false);
    setFilterOptions({ ...finalFilterOptions });
  }, [finalFilterOptions]);

  const handleCSVConfirm = async () => {
    setCsvConfirmModalVisible(false);
    setCsvProgressModalVisible(true);
    setCsvDownloading(true);

    try {
      // Make the API call without progress tracking
      await handleExportToCSV({
        customFallbackSuccessMessage: "CSV file downloaded successfully!",
        customFallbackErrorMessage:
          "Failed to download CSV file. Please try again.",
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      // Error handling is already done in handleExportToCSV
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleExportCSV = () => {
    setCsvConfirmModalVisible(true);
  };

  const handleCSVCancel = () => {
    setCsvConfirmModalVisible(false);
  };

  const handleCSVProgressClose = () => {
    setCsvProgressModalVisible(false);
  };

  useEffect(() => {
    handleApplyFilters();
  }, [search]);

  useEffect(() => {
    dispatch(fetchManagers());
  }, []);

  // Prevent tab closing during CSV export
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (csvDownloading) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue =
          "CSV export is in progress. Are you sure you want to leave?";
        return "CSV export is in progress. Are you sure you want to leave?";
      }
    };

    if (csvDownloading) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [csvDownloading]);

  return (
    <div className="csDashboardFilter">
      <div className="csDashboardFilter__search">
        <CustomSearchbar
          placeholder="Search by creator name, email or phone"
          searchQuery={search}
          setSearchQuery={(value) => {
            setSearch(value);
          }}
          style={{ marginBottom: 0, maxWidth: 500 }}
        />

        <Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            size="large"
          >
            Export CSV
          </Button>
          <Badge dot={isAnyFilterApplied}>
            <Button
              icon={<IoFilter />}
              onClick={() => {
                setFilterOptions({ ...finalFilterOptions });
                setOpenFilterDrawer(true);
              }}
            />
          </Badge>
        </Space>
      </div>
      <Drawer
        title="CSDashboardFilter"
        open={openFilterDrawer}
        destroyOnClose
        closable
        maskClosable
        className="csDashboardFilter__drawer"
        onClose={() => handleCancelFilters()}
        width={500}
        footer={
          <div className="csDashboardFilter__drawer__footer">
            <Button
              type="primary"
              onClick={() => {
                handleApplyFilters();
              }}
            >
              Apply
            </Button>
            <Button onClick={() => handleCancelFilters()}>Cancel</Button>
          </div>
        }
      >
        <div className="csDashboardFilter__drawer__body">
          <div className="csDashboardFilter__drawer__body__filters">
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}>Date Range</Typography.Title>
              <DatePicker.RangePicker
                value={[filterOptions.startDate, filterOptions.endDate]}
                onChange={(value) => {
                  setFilterOptions({
                    ...filterOptions,
                    startDate: value ? value[0]?.startOf("day") : null,
                    endDate: value ? value[1]?.endOf("day") : null,
                  });
                }}
                defaultPickerValue={RangePresets["Last 30 Days"]()}
                ranges={RangePresets}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                allowClear
              />
            </div>

            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}>Active Status</Typography.Title>
              <Select
                placeholder="Select Active Status"
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, activeStatus: value });
                }}
                value={filterOptions.activeStatus}
                allowClear
                options={ACTIVE_STATUS_OPTIONS}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Status type</Typography.Title>
              <Select
                placeholder="Select Status type"
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, platformSetups: value });
                }}
                mode="multiple"
                style={{ width: "100%" }}
                allowClear
                value={filterOptions.platformSetups}
                options={STATUS_TYPE_OPTIONS}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Account Manager</Typography.Title>
              <Select
                placeholder="Select Account Manager"
                mode="multiple"
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, accountManager: value });
                }}
                allowClear
                style={{ width: "100%" }}
                value={filterOptions.accountManager}
                options={managers.map((manager) => ({
                  label: manager.name,
                  value: manager._id,
                }))}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Tier</Typography.Title>
              <Select
                placeholder="Select Tier"
                mode="multiple"
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, tiers: value });
                }}
                style={{ width: "100%" }}
                allowClear
                value={filterOptions.tiers}
                options={TIER_OPTIONS}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Subscription</Typography.Title>
              <Select
                mode="multiple"
                placeholder="Select Subscription"
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, paymentPlans: value });
                }}
                style={{ width: "100%" }}
                allowClear
                value={filterOptions.paymentPlans}
                options={SUBSCRIPTION_OPTIONS}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Earning Range</Typography.Title>
              <div style={{ display: "flex" }}>
                <InputNumber
                  style={{ flex: 1 }}
                  placeholder="Min Earning"
                  onChange={(value) => {
                    setFilterOptions({
                      ...filterOptions,
                      minEarnings: value,
                    });
                  }}
                  allowClear
                  value={filterOptions.minEarnings}
                />
                <InputNumber
                  style={{ flex: 1 }}
                  placeholder="Max Earning"
                  onChange={(value) => {
                    setFilterOptions({
                      ...filterOptions,
                      maxEarnings: value,
                    });
                  }}
                  allowClear
                  value={filterOptions.maxEarnings}
                />
              </div>
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Plan expiry date</Typography.Title>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setFilterOptions({
                    ...filterOptions,
                    tierExpiryStartDate: value
                      ? value[0]?.startOf("day")
                      : null,
                    tierExpiryEndDate: value ? value[1]?.endOf("day") : null,
                  });
                }}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                ranges={RangePresets}
                allowClear
                value={[
                  filterOptions.tierExpiryStartDate,
                  filterOptions.tierExpiryEndDate,
                ]}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Last Renewal Date</Typography.Title>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setFilterOptions({
                    ...filterOptions,
                    tierLastRenewedAtStartDate: value
                      ? value[0]?.startOf("day")
                      : null,
                    tierLastRenewedAtEndDate: value
                      ? value[1]?.endOf("day")
                      : null,
                  });
                }}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                ranges={RangePresets}
                allowClear
                value={[
                  filterOptions.tierLastRenewedAtStartDate,
                  filterOptions.tierLastRenewedAtEndDate,
                ]}
              />
            </div>
            <div className="csDashboardFilter__drawer__body__filters__item">
              <Typography.Title level={5}> Last SignedIn Date</Typography.Title>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setFilterOptions({
                    ...filterOptions,
                    lastActiveAtStartDate: value
                      ? value[0]?.startOf("day")
                      : null,
                    lastActiveAtEndDate: value ? value[1]?.endOf("day") : null,
                  });
                }}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                ranges={RangePresets}
                allowClear
                value={[
                  filterOptions.lastActiveAtStartDate,
                  filterOptions.lastActiveAtEndDate,
                ]}
              />
            </div>
          </div>
        </div>
      </Drawer>
      <Modal
        title="Export CSV"
        visible={csvConfirmModalVisible}
        onOk={handleCSVConfirm}
        onCancel={handleCSVCancel}
        okText="Export"
        cancelText="Cancel"
        okButtonProps={{ loading: false }}
      >
        <p>Are you sure you want to export the coaches data as CSV?</p>
        <p style={{ color: "#666", fontSize: "14px" }}>
          This will download all coaches data based on your current filters and
          sorting.
        </p>
      </Modal>

      {/* CSV Export Progress Modal */}
      <Modal
        title="Exporting CSV"
        visible={csvProgressModalVisible}
        onCancel={handleCSVProgressClose}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={handleCSVProgressClose}
            disabled={csvDownloading}
          >
            {csvDownloading ? "Exporting..." : "Close"}
          </Button>,
        ]}
        closable={!csvDownloading}
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin size="large" spinning={csvDownloading} />
          <p style={{ marginTop: "16px", color: "#666", fontSize: "16px" }}>
            {csvDownloading
              ? "Please do not close this window while CSV export is in progress"
              : "CSV file has been downloaded successfully!"}
          </p>
        </div>
      </Modal>
    </div>
  );
};

CSDashboardFilter.propTypes = {
  setPaginationQuery: PropTypes.func.isRequired,
  handleExportToCSV: PropTypes.func.isRequired,
};

export default CSDashboardFilter;
